import { Search, SearchProps } from '@components/utility/inputs/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { alpha, Button, Chip, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import clsx from 'clsx';
import { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { PropsWithChildren, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.less';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

interface FiltersProps<TSearchBy extends string>
    extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
    searchInput?: string;
    onSearchChange?: (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    onSearchClear?: () => void;
    searchPlaceholder?: string;
    isOpen?: boolean;
    onFilterOpenToggle?: () => void;
    onClearFilterClick?: () => void;
    activeFilters?: {
        [key: string]: string[];
    };
    onChipClick?: (filter: string, value: string) => void;
    right?: ReactNode;
    getChipLabel?: (filter: string, item: string) => string;
    searchProps?: SearchProps<TSearchBy>;
    withFilterIcon?: boolean;
    emblaCarouselOptions?: EmblaOptionsType;
    activeFiltersPlacement?: 'top' | 'bottom';
    dividers?: boolean;
    gridCols?: number;
    searchSpan?: number;
    searchContainerClassName?: string;
}

const Filters = <TSearchBy extends string>({
    searchInput,
    onSearchChange,
    onSearchClear,
    isOpen,
    onFilterOpenToggle,
    onClearFilterClick,
    activeFilters,
    onChipClick,
    right,
    searchPlaceholder,
    getChipLabel,
    children,
    searchProps,
    withFilterIcon = true,
    emblaCarouselOptions = { active: true },
    activeFiltersPlacement = 'top',
    dividers = true,
    gridCols = 4,
    searchSpan = Math.floor(gridCols / 2),
    ...props
}: PropsWithChildren<FiltersProps<TSearchBy>>) => {
    useEmblaCarousel.globalOptions = emblaCarouselOptions;
    const remainingGridCols = gridCols - searchSpan - (right ? 1 : 0);
    const [isActiveFiltersOverflowing, setIsActiveFiltersOverflowing] = useState(false);
    const activeFiltersRef = useRef<HTMLDivElement>(null);
    const activeFiltersParentRef = useRef<HTMLDivElement>(null);
    const hasFilters = useMemo(() => activeFilters && Object.keys(activeFilters).length > 0, [activeFilters]);
    const [emblaRef, emblaApi] = useEmblaCarousel({ ...emblaCarouselOptions, active: hasFilters });
    const [, setSelectedSnap] = useState(0);
    const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
    const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

    const listenActiveFiltersScroll = () =>
        setIsActiveFiltersOverflowing(
            Boolean(
                activeFiltersRef.current &&
                    activeFiltersRef.current.offsetWidth + activeFiltersRef.current.scrollLeft <
                        activeFiltersRef.current.scrollWidth
            )
        );

    const onScrollChange = useCallback((emblaApi: EmblaCarouselType) => {
        const slidesInView = emblaApi.slidesInView();
        const lastIndex = emblaApi.slideNodes().length - 1;
        setIsActiveFiltersOverflowing(!slidesInView.includes(lastIndex));
    }, []);

    const handleNext = useCallback(() => {
        emblaApi?.scrollNext();
    }, [emblaApi]);

    const handlePrev = useCallback(() => {
        emblaApi?.scrollPrev();
    }, [emblaApi]);

    const updateScrollSnapState = useCallback((emblaApi: EmblaCarouselType) => {
        setSelectedSnap(emblaApi.selectedScrollSnap());
    }, []);

    const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
        setPrevBtnDisabled(!emblaApi.canScrollPrev());
        setNextBtnDisabled(!emblaApi.canScrollNext());
    }, []);

    useEffect(() => {
        if (hasFilters) {
            if (!emblaCarouselOptions) {
                if (!activeFiltersRef.current || !activeFiltersParentRef.current) return;
                const resizeObserver = new ResizeObserver(() => {
                    setIsActiveFiltersOverflowing(
                        Number(activeFiltersRef.current?.scrollWidth) >
                            Number(activeFiltersParentRef.current?.scrollWidth)
                    );
                });
                resizeObserver.observe(activeFiltersRef.current);
                return () => resizeObserver.disconnect();
            } else if (emblaApi && !!emblaCarouselOptions) {
                emblaApi.on('scroll', onScrollChange);
                emblaApi.on('slidesInView', onScrollChange);
                onScrollChange(emblaApi);

                updateScrollSnapState(emblaApi);
                emblaApi.on('select', updateScrollSnapState);
                emblaApi.on('reInit', updateScrollSnapState);

                onSelect(emblaApi);
                emblaApi.on('reInit', onSelect).on('select', onSelect);

                return () => {
                    emblaApi?.off('scroll', onScrollChange);
                    emblaApi?.off('slidesInView', onScrollChange);
                    emblaApi?.off('select', updateScrollSnapState);
                    emblaApi?.off('reInit', updateScrollSnapState);
                    emblaApi?.off('reInit', onSelect);
                    emblaApi?.off('select', onSelect);
                };
            }
        }
    }, [
        activeFilters,
        activeFiltersParentRef,
        activeFiltersRef,
        emblaApi,
        emblaCarouselOptions,
        hasFilters,
        onScrollChange,
        onSelect,
        updateScrollSnapState,
    ]);

    const ActiveFiltersElement = useMemo(
        () =>
            activeFilters ? (
                <div className="min-h-10 overflow-auto">
                    <div className="flex gap-2">
                        <p className="text-gray-400 text-xs">Active filters</p>
                        <Button
                            variant="text"
                            size="small"
                            disabled={!hasFilters}
                            className="h-fit p-0"
                            onClick={onClearFilterClick}
                        >
                            <Typography fontWeight={600} fontSize={12}>
                                Clear filters
                            </Typography>
                        </Button>
                    </div>
                    {!emblaCarouselOptions ? (
                        <div ref={activeFiltersParentRef} className={'relative w-full'}>
                            <div
                                ref={activeFiltersRef}
                                className="flex gap-2 mt-2 overflow-x-auto w-full"
                                style={{ scrollbarWidth: 'none' }}
                                onScroll={listenActiveFiltersScroll}
                            >
                                {hasFilters
                                    ? Object.keys(activeFilters).map((filter) =>
                                          activeFilters[filter].map((item) => (
                                              <Chip
                                                  key={'active-filter-' + filter + '-' + item?.trim()}
                                                  id={item}
                                                  label={getChipLabel?.(filter, item) ?? item}
                                                  onDelete={() => onChipClick?.(filter, item)}
                                                  variant="outlined"
                                                  color="primary"
                                                  className="text-nowrap"
                                              />
                                          ))
                                      )
                                    : null}
                            </div>
                            {isActiveFiltersOverflowing ? <div className={styles.ActiveFilters} /> : null}
                        </div>
                    ) : (
                        <div className="relative flex items-center mt-2 overflow-hidden w-full">
                            {!nextBtnDisabled || !prevBtnDisabled ? (
                                <IconButton
                                    onClick={handlePrev}
                                    disabled={prevBtnDisabled}
                                    color="black"
                                    size="small"
                                    className="flex-[0_0_auto]"
                                >
                                    <ArrowBackIosNewIcon fontSize="small" className="w-[20px]" />
                                </IconButton>
                            ) : null}
                            <div className="relative flex-auto min-w-0">
                                <div className="overflow-hidden" ref={emblaRef}>
                                    <div
                                        className={clsx('flex gap-2 touch-pan-y touch-pinch-zoom', {
                                            'cursor-grab active:cursor-grabbing': hasFilters,
                                        })}
                                    >
                                        {hasFilters ? (
                                            Object.keys(activeFilters).map((filter) =>
                                                activeFilters[filter].map((item) => (
                                                    <Chip
                                                        key={'active-filter-' + filter + '-' + item?.trim()}
                                                        id={item}
                                                        label={getChipLabel?.(filter, item) ?? item}
                                                        onDelete={() => onChipClick?.(filter, item)}
                                                        variant="outlined"
                                                        color="primary"
                                                        className="text-nowrap cursor-grab active:cursor-grabbing"
                                                    />
                                                ))
                                            )
                                        ) : (
                                            <Chip
                                                key={'active-filter-none'}
                                                id="none"
                                                label="No active filters"
                                                variant="outlined"
                                                className="w-fit"
                                                disabled
                                            />
                                        )}
                                    </div>
                                </div>
                                <div
                                    className={clsx(
                                        'absolute top-0 right-0 h-full w-[5%] bg-gradient-to-r from-transparent to-white pointer-events-none',
                                        {
                                            hidden: !isActiveFiltersOverflowing,
                                        }
                                    )}
                                />
                            </div>
                            {!nextBtnDisabled || !prevBtnDisabled ? (
                                <IconButton
                                    onClick={handleNext}
                                    disabled={nextBtnDisabled}
                                    color="black"
                                    size="small"
                                    className="flex-[0_0_auto]"
                                >
                                    <ArrowForwardIosIcon fontSize="small" className="w-[20px]" />
                                </IconButton>
                            ) : null}
                        </div>
                    )}
                </div>
            ) : null,
        [
            activeFilters,
            emblaCarouselOptions,
            emblaRef,
            getChipLabel,
            handleNext,
            handlePrev,
            hasFilters,
            isActiveFiltersOverflowing,
            nextBtnDisabled,
            onChipClick,
            onClearFilterClick,
            prevBtnDisabled,
        ]
    );

    return (
        <div {...props} className={clsx('bg-white px-5 pt-5 rounded flex flex-col gap-3', props.className)}>
            <div
                className="grid gap-x-2.5 gap-y-3"
                style={{
                    gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
                }}
            >
                <div
                    className={clsx(
                        'flex items-center h-full',
                        { 'gap-1.5': withFilterIcon },
                        props.searchContainerClassName
                    )}
                    style={{
                        gridColumn: `span ${searchSpan} / span ${searchSpan}`,
                    }}
                >
                    <Tooltip title={searchPlaceholder}>
                        <>
                            <Search
                                value={searchInput}
                                fullWidth
                                onChange={onSearchChange}
                                slotProps={{
                                    input: {
                                        endAdornment: searchInput ? (
                                            <IconButton edge="end" size="small" onClick={onSearchClear}>
                                                <ClearIcon />
                                            </IconButton>
                                        ) : null,
                                    },
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                    },
                                }}
                                placeholder={searchPlaceholder}
                                {...searchProps}
                            />
                        </>
                    </Tooltip>
                    {withFilterIcon ? (
                        <IconButton onClick={onFilterOpenToggle}>
                            {isOpen ? (
                                <FilterAltIcon sx={{ color: (theme) => alpha(theme.palette.black.main, 0.56) }} />
                            ) : (
                                <FilterAltOutlinedIcon
                                    sx={{ color: (theme) => alpha(theme.palette.black.main, 0.56) }}
                                />
                            )}
                        </IconButton>
                    ) : null}
                </div>
                {activeFiltersPlacement === 'top' ? (
                    <div
                        style={{
                            gridColumn: !right
                                ? `span ${Math.floor(gridCols / 2)} / span ${Math.floor(gridCols / 2)}`
                                : `span ${gridCols - Math.floor(gridCols / 2) - 1} / span ${
                                      gridCols - Math.floor(gridCols / 2) - 1
                                  }`,
                        }}
                    >
                        {ActiveFiltersElement}
                    </div>
                ) : remainingGridCols > 0 ? (
                    <div
                        style={{
                            gridColumn: `span ${remainingGridCols} / span ${remainingGridCols}`,
                        }}
                    />
                ) : null}
                {right ? (
                    <div style={{ gridColumn: 'span 1/span 1', gridColumnEnd: gridCols + 1 }}>
                        {right ? right : null}
                    </div>
                ) : null}
            </div>
            <div className={clsx({ hidden: !isOpen && withFilterIcon })}>
                <>
                    {children ? (
                        <>
                            {dividers ? <Divider /> : null}
                            {children}
                        </>
                    ) : null}
                    {dividers ? <Divider /> : null}
                </>
            </div>
            {activeFiltersPlacement === 'bottom' ? ActiveFiltersElement : null}
        </div>
    );
};

export { Filters };
